import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
  InputAdornment,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountCircle,
  Settings,
  Palette,
  Logout,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  VisibilityOff as ViewIcon,
  Groups as TeamsIcon,
  Layers as DefaultConfigIcon,
  Help as HelpIcon
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../hooks/useDashboard';
import { useResponsive, getMobileStyles } from '../utils/mobileUtils';
import { useSearch } from '../hooks/useSearch';
import { useNavigate } from 'react-router-dom';
import ThemeCustomizer from './Theme/ThemeCustomizer';
import { GlobalSettings } from '../types';
import defaultLogo from '../assets/images/TrueDotDESIGN.png';

interface DashboardHeaderProps {
  title: string;
  icon: string;
  editMode: boolean;
  onToggleEditMode: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  title,
  icon,
  editMode,
  onToggleEditMode
}) => {
  const { user, logout } = useAuth();
  const { config, updateConfig, resetConfig } = useDashboard();
  const { isMobile, isSmallMobile } = useResponsive();
  const mobileStyles = getMobileStyles(isMobile);
  const navigate = useNavigate();
  
  // Get theme colors
  const themeColors = config?.theme?.customColors || {} as NonNullable<import('../types').DashboardTheme['customColors']>;
  const {
    searchQuery,
    setSearchQuery,
    clearSearch,
    isSearching
  } = useSearch({
    debounceMs: 300,
    minQueryLength: 2,
    searchInGroups: true
  });
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [themeCustomizerOpen, setThemeCustomizerOpen] = useState(false);
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);

  // Load global settings for logo
  React.useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const response = await fetch('/api/users/global-settings');
        const data = await response.json();
        if (data.success && data.data) {
          setGlobalSettings(data.data.globalSettings);
        }
      } catch (error) {
        console.error('Failed to load global settings:', error);
        // Use default settings on error
      }
    };
    
    loadGlobalSettings();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  // Title editing functionality removed - title is static



  return (
    <AppBar 
      position="sticky" 
      elevation={1}
      sx={{
        height: mobileStyles.header.height,
      }}
    >
      <Toolbar 
        sx={{
          minHeight: `${mobileStyles.header.height}px !important`,
          px: mobileStyles.header.padding,
        }}
      >
        {/* Center - Logo and Title */}
        <Box sx={{ 
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pointerEvents: 'none', // Prevent interference with right-side controls
          zIndex: 1
        }}>
          {/* Logo */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
            }}
          >
            {/* Show the company logo */}
            <img
              src={
                globalSettings?.dashboardLogo?.type === 'image' && globalSettings.dashboardLogo.url
                  ? globalSettings.dashboardLogo.url
                  : defaultLogo
              }
              alt="Company Logo"
              style={{
                height: isMobile ? 32 : 40,
                display: isSmallMobile ? 'none' : 'block',
                marginRight: 8,
                opacity: 0.9,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                display: isSmallMobile ? 'none' : 'block',
                fontSize: isMobile ? '1rem' : '1.25rem',
                textAlign: 'center'
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Search, Edit Mode, and User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
          {/* Compact Search Bar */}
          {config?.layout.showSearch && (
            <TextField
              size="small"
              placeholder={isMobile ? "Search..." : "Search tiles..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  clearSearch();
                }
              }}
              sx={{
                width: isMobile ? 140 : 200,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'inherit',
                  fontSize: '0.875rem',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={clearSearch}
                      size="small"
                      sx={{ color: 'inherit', opacity: 0.7 }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}


          {/* User Menu */}
          <IconButton
            onClick={handleMenuOpen}
            color="inherit"
            aria-label="account menu"
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32,
                backgroundColor: 'primary.main',
                fontSize: '0.875rem'
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || <AccountCircle />}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                },
              },
            }}
          >
            <MenuItem disabled>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.role}
                </Typography>
              </ListItemText>
            </MenuItem>
            
            <Divider />

            {/* User Management - only show for superuser and admin */}
            {(user?.role === 'superuser' || user?.role === 'admin') && (
              <MenuItem onClick={() => {
                navigate('/users');
                handleMenuClose();
              }}>
                <ListItemIcon>
                  {user?.role === 'superuser' ? <AdminIcon fontSize="small" /> : <PeopleIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>User Management</ListItemText>
              </MenuItem>
            )}

            {/* Team Management - only show for superuser and admin */}
            {(user?.role === 'superuser' || user?.role === 'admin') && (
              <MenuItem onClick={() => {
                navigate('/teams');
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <TeamsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Team Management</ListItemText>
              </MenuItem>
            )}

            {/* Default Config - only show for superuser */}
            {user?.role === 'superuser' && (
              <MenuItem onClick={() => {
                navigate('/default-config');
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <DefaultConfigIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Default Config</ListItemText>
              </MenuItem>
            )}
            
            <MenuItem onClick={() => {
              onToggleEditMode();
              handleMenuClose();
            }}>
              <ListItemIcon>
                {editMode ? <ViewIcon fontSize="small" /> : <EditIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => {
              setThemeCustomizerOpen(true);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <Palette fontSize="small" />
              </ListItemIcon>
              <ListItemText>Theme</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => {
              navigate('/help');
              handleMenuClose();
            }}>
              <ListItemIcon>
                <HelpIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Help</ListItemText>
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Theme Customizer Dialog */}
      <ThemeCustomizer
        open={themeCustomizerOpen}
        onClose={() => setThemeCustomizerOpen(false)}
      />
    </AppBar>
  );
};

export default DashboardHeader;